/**
 * Helpers for image info and meta data
 */
/* eslint import/order: ["warn", {"newlines-between": "always-and-inside-groups"}] */
import {
    formatImageSrcParams,
    getCanvasXStep,
    getCanvasYStep
} from '@/helpers/masonry.js';

import {
    getBaseUrl
} from '@/helpers/index.js';

import {
    getCoverImageSrc,
    getIngressImageSrc
} from '@/helpers/image-resources.js';


export const getCoverImageInfo = function (item) {
    return item?.CoverImageInfo
        || item?.VideoPlayer?.CoverImageInfo
        || item?.AudioPlayer?.CoverImageInfo;
};


export const getIngressImageInfo = function (item) {
    return item?.IngressImageInfo
        || item?.VideoPlayer?.IngressImageInfo
        || item?.AudioPlayer?.IngressImageInfo;
};


/**
 * Get ImageInfo or CoverImageInfo or IngressImageInfo.
 * TODO: deprecate `useAnyImageInfo`, fix callers to specify ImageInfo source.
 */
export const getImageInfo = function (item, useAnyImageInfo) {
    if (!useAnyImageInfo) {
        return item?.ImageInfo;
    }
    else {
        return item && (
            item.ImageInfo
            || getCoverImageInfo(item)
            || getIngressImageInfo(item));
    }
};


/**
 * Get Width, Height from ImageInfo
 */
export const getImageWidthHeight = function (item) {
    const imageInfo = getImageInfo(item, true); // FIXME: ImageInfo, CoverImageInfo, IngressImageInfo?
    const width = imageInfo && imageInfo.Width;
    const height = imageInfo && imageInfo.Height;
    return { width, height };
};


export const getViewportResolution = function (vwvhString) {

    const screenWidth = (process.client && window.innerWidth) || 1024;
    const screenHeight = (process.client && window.innerHeight) || 768;
    const pixelRatio = (process.client && window.devicePixelRatio) || 1;

    const value = {};
    if (vwvhString.search('vh|vw') > 0) {
        // parse viewport w / h
        const mh = vwvhString.match(/(.*)vh/);
        const vh = mh && parseInt(mh[1], 10);
        const mw = vwvhString.match(/(.*)vw/);
        const vw = mw && parseInt(mw[1], 10);
        // calculate pixels from the percentage, also account for device pixel ratio
        if (vh) {
            value.height = Math.round((vh / 100.0) * screenHeight * pixelRatio);
        }
        else if (vw) {
            value.width = Math.round((vw / 100.0) * screenWidth  * pixelRatio);
        }
    }
    return value;
};


/**
 * Get Image aspect ratio from ImageInfo Width / Height.
 */
export const getImageAspectRatio = function (item) {
    const { width, height } = getImageWidthHeight(item);

    if (typeof width !== 'number' || typeof height !== 'number') {
        return null;
    }

    return width / height;
};


/**
 * Returns whether `item` image is `portrait` or `landscape`.
 * Prefers `item.ImageInfo` / `IngressImageInfo` / `CoverImageInfo` to `resolution`.
 */
export const getImageOrientation = function (item, resolution) {
    let orientation = '';

    let ratio = getImageAspectRatio(item);
    if (!ratio && resolution && resolution.x && resolution.y) {
        ratio = resolution.x / resolution.y;
    }

    if (ratio) {
        if (ratio <= 0.5) {
            orientation = 'portrait narrow';
        }
        else if (ratio <= 1) {
            orientation = 'portrait';
        }
        else if (ratio <= 2) {
            orientation = 'landscape';
        }
        else {
            orientation = 'landscape wide';
        }
    }

    return orientation;
};


export const getImageCaption = function (item) {
    const imageInfo = getImageInfo(item);
    return imageInfo?.Caption || item?.ImageCaption;
};


export const getImageCreditLine = function (item) {
    const imageInfo = getImageInfo(item);
    return imageInfo?.CreditLine || item?.ImageCreditLine;
};


export const getImageCopyright = function (item) {
    const imageInfo = getImageInfo(item);
    return imageInfo?.Copyright || item?.ImageCopyright;
};


export const getImageLicense = function (item) {
    const imageInfo = getImageInfo(item);
    return imageInfo?.License || item?.ImageLicense;
};


/**
 * Get the character "?" or "&" that leads the image url for parameters.
 */
export const getImageUrlParamChar = function (imageUrl) {
    return (imageUrl && imageUrl.match(/\?[^/]*$/)) ? '&' : '?'; // does the image url have ? already or should there be '&'
};


/**
 * Combine image url (path) with image attributes to contruct complete src.
 */
export const constructImageSrc = function (imageUrl, imageAttrs) {
    if (imageUrl) {
        const { resolution, format, zoom, viewerZoom, ratio, urlParams, cacheBust, zoomMode, missingImage } = imageAttrs;
        const params = formatImageSrcParams(resolution, format, zoom, viewerZoom, ratio, urlParams, cacheBust, zoomMode, missingImage);
        const baseUrl = getBaseUrl();
        const joinChar = getImageUrlParamChar(imageUrl);
        let src = '';
        if (!(imageUrl.match(/^http/))) {
            src += baseUrl;
        }
        src += `${imageUrl}${joinChar}${params}`;
        return src;
    }
};


/**
 * Combine image url (path) with image attributes.
 * Adds url params to the given `imageUrl`.
 * Takes device screen geometry into account for `imageAttrs` vw and vh resolution.
 * Refactored merge of `constructImageSrc`, `formatImageSrcParams`, and `getMasonryCanvasResolution` helpers.
 */
export const constructImageSrc2 = function (imageUrl, imageAttrs) {
    if (!imageUrl) {
        return;
    }

    const { format, zoom, ratio, urlParams, cacheBust, zoomMode, missingImage } = imageAttrs;
    const screenWidth = (process.client && window.innerWidth) || 1024;

    // set default X resolution, if neither is given
    const resolution = _.extend({x: 500}, imageAttrs.resolution);

    // Parse "vh" and "vw" from resolution string.
    // String value can be anything, except "vw", "vh" are parsed for numeric value.
    // resolution.x and .y are modified in place from string to number.
    if (typeof resolution.x === 'string') {
        const vp = getViewportResolution(resolution.x);
        if (vp.height) {
            resolution.x = vp.height;
        }
        else if (vp.width) {
            resolution.x = vp.width;

            // given that "50vw" may in mobile view show the image screen-wide
            if (screenWidth < 576 && resolution.x < 500) {
                resolution.x = 500;
            }
            else if (screenWidth < 769 && resolution.x < 800) {
                resolution.x = 800;
            }
            else {
                // add some leeway for margin to upscale image resolution
                resolution.x += 90;
            }
        }
    }
    if (typeof resolution.y === 'string') {
        const vp = getViewportResolution(resolution.y);
        if (vp.height) {
            resolution.y = vp.height;
        }
        else if (vp.width) {
            resolution.y = vp.width;
        }
        // Y can be a percentage, but it will be accounted for after X resolution step is calculated
    }

    // Set resolution limits --
    // Resolution can be anything, but for cache optimisation,
    // the image is queried from backend in resolution steps.
    // When resolution.fixed is set, no step adjustment is made.
    let x;
    if (resolution.x && !resolution.fixed) {
        x = getCanvasXStep(resolution.x);
    }
    else {
        x = resolution.x;
    }

    let y;
    if (typeof resolution.y === 'number') {
        y = resolution.y;

        // calculate new height
        if (ratio) {
            y = Math.round(x * ratio);
        }
    }
    // Y can be a percentage
    else if (typeof resolution.y === 'string' && resolution.y.match(/\d+%/)) {
        const perc = parseInt(resolution.y.replace('%', ''), 10) / 100;
        if (typeof x === 'number') {
            y = Math.round(x * perc);
        }
    }
    // get Y step, unless resolution.fixed is set
    if (y && !resolution.fixed) {
        y = getCanvasYStep(y);
    }


    // construct image url params
    const params = [];
    if (format) {
        // default format is always jpg
        params.push(`Format=${format}`);
    }
    if (x) {
        params.push(`CanvasX=${x}`);
    }
    if (y) {
        params.push(`CanvasY=${y}`);
    }
    if (zoom) {
        params.push(`Zoom=${zoom}`);
    }
    if (urlParams) {
        params.push(urlParams);
    }
    if (zoomMode) {
        params.push(`ZoomMode=${zoomMode}`);
    }
    if (missingImage) {
        params.push(`MissingImage=${missingImage}`);
    }
    if (cacheBust) {
        params.push(getCacheBustParam(true));
    }
    const paramsStr = _.compact(params).join('&');

    const baseUrl = getBaseUrl();
    const joinChar = getImageUrlParamChar(imageUrl);
    let src = '';
    if (!(imageUrl.match(/^http/))) {
        src += baseUrl;
    }
    src += `${imageUrl}${joinChar}${paramsStr}`;
    return src;
};


/**
 * Image url (path), without base.

    ImageRequest Parameters:

        CanvasX
        CanvasY
        CanvasXMin
        CanvasYMin
        CanvasXMax
        CanvasYMax
        ClipX
        ClipY
        CornerRadius
        RotateDegrees
        OffsetX
        OffsetY
        Resolution
        Zoom
        BorderColor
        BackgroundColor

 */
export const getImageUrl = function (item) {
    if (!item) {
        return null;
    }
    let imageUrl = '';
    // try ImageInfo, from item or Artwork wrapper
    let imageInfo = getImageInfo(item, true); // FIXME: ImageInfo, CoverImageInfo, IngressImageInfo?
    if (!imageInfo && item.Artwork) {
        imageInfo = getImageInfo(item.Artwork);
    }
    // prefer ImageInfo.Url
    if (imageInfo?.Url) {
        imageUrl = imageInfo.Url;
    }
    // then, if this is FeatureArticle ImagePart
    else if (typeof item.Caption !== 'undefined' && item.Id) {
        imageUrl = `/svc/images/${item.Id}`;
    }
    // then try _links.image
    else if (item._links?.image?.href) {
        imageUrl = item._links.image.href;
    }
    // cover image
    if (!imageUrl) {
        imageUrl = getCoverImageSrc(item);
    }
    // ingress image
    if (!imageUrl) {
        imageUrl = getIngressImageSrc(item);
    }
    // video thumbnail from external video service
    if (!imageUrl && item.VideoPlayer) {
        imageUrl = item.VideoPlayer.VideoInfo?.ThumbnailUrl;
    }
    // finally try deprecated ImageUrl
    if (!imageUrl && item.ImageUrl) {
        imageUrl = item.ImageUrl;
    }

    return imageUrl;
};


/**
 * Full image src, including base and query parameters
 */
export const getImageSrc = function (item, imageAttrs) {
    const imageUrl = getImageUrl(item);

    if (!imageUrl) {
        return '';
    }
    if (!imageUrl.match('/svc')) {
        return imageUrl;
    }

    const ratio = imageAttrs?.ratio || getImageAspectRatio(item);
    const attrs = _.extend({}, imageAttrs, {ratio});

    return constructImageSrc(imageUrl, attrs);
};


/**
 * Get timestamp into the future when to bust browser image cache by updating the timestamp.
 */
export const getCacheBustParam = function (editMode) {
    if (!editMode) {
        return '';
    }
    const ts = 'ts=' + new Date().getMilliseconds();
    return ts;
};
